import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuditLogEntry } from '../../store/actions/AuditLogActions';
import Badge from '../shared/TailwindComponents/Badge';
import { ensureUTCString, formatDateTime } from '../../utils/functions';
import {
  PiUserLight,
  PiClockLight,
  PiArrowRightLight,
} from 'react-icons/pi';

interface AuditLogDetailModalProps {
  entry: AuditLogEntry;
  onClose: () => void;
}

const getEntityBadgeColor = (entityType: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
  const colorMap: Record<string, 'gray' | 'blue' | 'yellow' | 'green' | 'red'> = {
    client: 'blue',
    Client: 'blue',
    ClientBilling: 'blue',
    ClientDocument: 'blue',
    ClientLawyer: 'blue',
    matter: 'green',
    Matter: 'green',
    MatterClient: 'green',
    MatterCollaborator: 'green',
    MatterDocument: 'green',
    MatterDraft: 'green',
    MatterInsight: 'green',
    MatterNote: 'green',
    MatterQuestion: 'green',
    MatterSource: 'green',
    MatterTask: 'green',
    file: 'gray',
    File: 'gray',
    folder: 'gray',
    Folder: 'gray',
    task: 'yellow',
    Task: 'yellow',
    review: 'blue',
    review_session: 'blue',
    playbook: 'green',
    research: 'blue',
    table: 'green',
    table_document: 'green',
    template: 'gray',
    Device: 'gray',
    User: 'yellow',
    Organization: 'yellow',
    Team: 'blue',
    TeamMember: 'blue',
    Department: 'blue',
    DepartmentMember: 'blue',
    Role: 'yellow',
    Permission: 'yellow',
    RecycleBin: 'red',
    Tag: 'gray',
    datasource: 'gray',
    CalendarEvent: 'yellow',
  };
  return colorMap[entityType] || 'gray';
};

const getActionBadgeColor = (action: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
  if (action.includes('creat') || action.includes('upload') || action.includes('restor') || action.includes('add')) return 'green';
  if (action.includes('delet') || action.includes('remov') || action.includes('permanent') || action.includes('logout')) return 'red';
  if (action.includes('updat') || action.includes('chang') || action.includes('renam') || action.includes('process') || action === 'update' || action === 'save' || action.includes('preference') || action.includes('info')) return 'blue';
  if (action.includes('archiv') || action.includes('reset')) return 'yellow';
  return 'gray';
};

const formatLabel = (str: string) => str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Return true for keys that look like internal identifiers (e.g. "tag_id", "Attachment ID", "uuid"). */
const isIdKey = (key: string): boolean => {
  const k = key.toLowerCase().replace(/[\s_-]+/g, '_');
  return (
    k === 'id' ||
    k === 'uuid' ||
    k.endsWith('_id') ||
    k.endsWith('_uuid') ||
    k.endsWith('_encrypted') ||
    k === 'ip_address' ||
    k === 'email' ||
    k === 'user_email'
  );
};

const AuditLogDetailModal: React.FC<AuditLogDetailModalProps> = ({ entry, onClose }) => {
  const { t } = useTranslation();

  const timestamp = entry.created_at ? formatDateTime(entry.created_at) : '—';

  /** Clean up raw backend details strings for display. */
  const formatDetails = (raw: string): string => {
    const logoutMatch = raw.match(/^Logged out all sessions \(count=(\d+)\)$/);
    if (logoutMatch) {
      const count = parseInt(logoutMatch[1], 10);
      if (count === 0) return 'Logged out all sessions (no active sessions)';
      if (count === 1) return 'Logged out 1 session across all devices';
      return `Logged out ${count} sessions across all devices`;
    }
    const permDelMatch = raw.match(/^Permanently deleted: (\d+) folder\(s\), (\d+) file\(s\)$/);
    if (permDelMatch) {
      const folders = parseInt(permDelMatch[1], 10);
      const files = parseInt(permDelMatch[2], 10);
      const parts: string[] = [];
      if (folders) parts.push(`${folders} folder${folders !== 1 ? 's' : ''}`);
      if (files) parts.push(`${files} file${files !== 1 ? 's' : ''}`);
      return parts.length ? `Permanently deleted ${parts.join(' and ')}` : t('Permanently deleted items');
    }
    const sessionMatch = raw.match(/^Logged out session (.{8})$/);
    if (sessionMatch) return 'Logged out a session';
    return raw;
  };

  /** Format a JSON value for human-readable display, handling nested objects/arrays. */
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) {
      return value.map(v =>
        typeof v === 'object' && v !== null
          ? Object.entries(v as Record<string, unknown>).filter(([k]) => !isIdKey(k)).map(([k, val]) => `${formatLabel(k)}: ${val}`).join(', ') || JSON.stringify(v)
          : String(v)
      ).join('; ');
    }
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).filter(([k]) => !isIdKey(k)).map(([k, val]) => `${formatLabel(k)}: ${val}`).join(', ');
    }
    return String(value);
  };

  const hasChanges = entry.before_json && entry.after_json;
  const beforeJson = entry.before_json || {};
  const afterJson = entry.after_json || {};
  const detailsJson = entry.details_json || {};

  // Collect all changed field keys, excluding ID-like fields
  const changedFields = hasChanges
    ? Array.from(new Set([...Object.keys(beforeJson), ...Object.keys(afterJson)])).filter(k => !isIdKey(k))
    : [];

  return (
    <div className="w-[660px] max-w-[90vw] max-h-[70vh] overflow-y-auto px-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge color={getActionBadgeColor(entry.action)}>
            {formatLabel(entry.action)}
          </Badge>
          <Badge color={getEntityBadgeColor(entry.entity_type)}>
            {formatLabel(entry.entity_type)}
          </Badge>
        </div>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-1 gap-3 mb-6">
        <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
          <PiUserLight className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {entry.user_name || t('System')}
          </span>
        </div>
        <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
          <PiClockLight className="w-4 h-4 flex-shrink-0" />
          <span>{timestamp}</span>
        </div>
        {entry.entity_created_by_name && (
          <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
            <PiUserLight className="w-4 h-4 flex-shrink-0" />
            <span className="text-gray-500">{t('Created by')}</span>
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {entry.entity_created_by_name}
            </span>
            {entry.entity_created_by_role && (
              <Badge color={entry.entity_created_by_role === 'Admin' ? 'yellow' : 'gray'} className="text-[10px] px-1.5 py-0">
                {entry.entity_created_by_role}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Details text */}
      {entry.details && (
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('Summary')}
          </h4>
          <p className="text-sm text-gray-700 dark:text-gray-300">{formatDetails(entry.details)}</p>
        </div>
      )}

      {/* Details JSON */}
      {Object.keys(detailsJson).length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('Details')}
          </h4>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
            {Object.entries(detailsJson).filter(([key]) => !isIdKey(key)).map(([key, value]) => (
              <div key={key} className="flex items-start px-3.5 py-2.5">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-40 flex-shrink-0 pt-0.5">
                  {formatLabel(key)}
                </span>
                <span className="text-sm text-gray-800 dark:text-gray-200 break-words min-w-0">
                  {formatValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Changes diff table */}
      {hasChanges && changedFields.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('Changes')}
          </h4>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-3.5 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 w-1/3">
                    {t('Field')}
                  </th>
                  <th className="text-left px-3.5 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 w-1/3">
                    {t('Before')}
                  </th>
                  <th className="text-left px-3.5 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 w-1/3">
                    {t('After')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {changedFields.map((field) => {
                  const before = beforeJson[field];
                  const after = afterJson[field];
                  const changed = String(before ?? '') !== String(after ?? '');
                  return (
                    <tr key={field}>
                      <td className="px-3.5 py-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                        {formatLabel(field)}
                      </td>
                      <td className={`px-3.5 py-2 text-xs break-words ${changed ? 'text-danger-600 dark:text-danger-400 bg-danger-50/50 dark:bg-danger-900/10' : 'text-gray-600 dark:text-gray-400'}`}>
                        {before !== undefined && before !== null ? String(before) : <span className="text-gray-300 dark:text-gray-600 italic">—</span>}
                      </td>
                      <td className="px-3.5 py-2 text-xs break-words">
                        <div className={`flex items-center gap-1 ${changed ? 'text-success-600 dark:text-success-400 bg-success-50/50 dark:bg-success-900/10 -mx-3.5 px-3.5 -my-2 py-2' : 'text-gray-600 dark:text-gray-400'}`}>
                          {changed && <PiArrowRightLight className="w-3 h-3 flex-shrink-0 opacity-50" />}
                          {after !== undefined && after !== null ? String(after) : <span className="text-gray-300 dark:text-gray-600 italic">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* After-only state (for creates) */}
      {!entry.before_json && entry.after_json && Object.keys(afterJson).length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('Created With')}
          </h4>
          <div className="bg-success-50/50 dark:bg-success-900/10 rounded-lg border border-success-200 dark:border-success-800/30 divide-y divide-success-200 dark:divide-success-800/30">
            {Object.entries(afterJson).filter(([key]) => !isIdKey(key)).map(([key, value]) => (
              <div key={key} className="flex items-start px-3.5 py-2.5">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-40 flex-shrink-0 pt-0.5">
                  {formatLabel(key)}
                </span>
                <span className="text-sm text-success-700 dark:text-success-400 break-words min-w-0">
                  {formatValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Before-only state (for deletes) */}
      {entry.before_json && !entry.after_json && Object.keys(beforeJson).length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            {t('Deleted State')}
          </h4>
          <div className="bg-danger-50/50 dark:bg-danger-900/10 rounded-lg border border-danger-200 dark:border-danger-800/30 divide-y divide-danger-200 dark:divide-danger-800/30">
            {Object.entries(beforeJson).filter(([key]) => !isIdKey(key)).map(([key, value]) => (
              <div key={key} className="flex items-start px-3.5 py-2.5">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-40 flex-shrink-0 pt-0.5">
                  {formatLabel(key)}
                </span>
                <span className="text-sm text-danger-700 dark:text-danger-400 break-words min-w-0">
                  {formatValue(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state for legacy entries */}
      {!entry.details && Object.keys(detailsJson).length === 0 && !entry.before_json && !entry.after_json && (
        <div className="text-center py-6 text-gray-400">
          <p className="text-sm">{t('No additional details recorded for this entry.')}</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogDetailModal;
